import { cn } from '@workspace/lib/utils';
import { buildDatUrl, resolveAlt, resolveSource } from '@workspace/lib/picture.utils';
import type { PictureSource } from '@workspace/lib/picture.utils';

export interface PictureProps {
  image?: PictureSource;
  src?: string;
  alt?: string;
  desktopWidth: number;
  desktopHeight: number;
  tabletWidth?: number;
  tabletHeight?: number;
  tabletBreakpoint?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  mobileBreakpoint?: number;
  className?: string;
  imgClassName?: string;
  draggable?: boolean;
  ariaHidden?: boolean;
  priority?: boolean;
  loading?: 'eager' | 'lazy';
  fill?: boolean;
}

export function Picture({
  image,
  src,
  alt,
  desktopWidth,
  desktopHeight,
  tabletWidth,
  tabletHeight,
  tabletBreakpoint = 1280,
  mobileWidth,
  mobileHeight,
  mobileBreakpoint = 768,
  className,
  imgClassName,
  draggable,
  ariaHidden,
  priority = false,
  loading,
  fill = false,
}: PictureProps) {
  const resolved = image ? resolveSource(image) : undefined;
  const baseUrl = resolved?.baseUrl || src || '';
  const shouldTransform = !!resolved?.isBynder && !resolved.isSvg && !!resolved.baseUrl;

  if (!baseUrl) return null;
  const buildSrc = (w: number, h: number) => (shouldTransform ? buildDatUrl(baseUrl, w, h) : baseUrl);

  const effectiveMobileWidth = mobileWidth ?? desktopWidth;
  const effectiveMobileHeight = mobileHeight ?? desktopHeight;
  const hasTabletSource = !!(tabletWidth && tabletHeight);
  const hasMobileSource =
    hasTabletSource || effectiveMobileWidth !== desktopWidth || effectiveMobileHeight !== desktopHeight;

  const imgSrc = hasMobileSource
    ? buildSrc(effectiveMobileWidth, effectiveMobileHeight)
    : buildSrc(desktopWidth, desktopHeight);
  const imgWidth = hasMobileSource ? effectiveMobileWidth : desktopWidth;
  const imgHeight = hasMobileSource ? effectiveMobileHeight : desktopHeight;

  return (
    <picture className={cn(fill && 'absolute inset-0 size-full', className)}>
      {hasMobileSource && (
        <source
          media={`(min-width: ${hasTabletSource ? tabletBreakpoint : mobileBreakpoint}px)`}
          srcSet={buildSrc(desktopWidth, desktopHeight)}
          width={desktopWidth}
          height={desktopHeight}
        />
      )}
      {hasTabletSource && (
        <source
          media={`(min-width: ${mobileBreakpoint}px)`}
          srcSet={buildSrc(tabletWidth!, tabletHeight!)}
          width={tabletWidth!}
          height={tabletHeight!}
        />
      )}
      <img
        src={imgSrc}
        alt={image ? resolveAlt(image, alt) : (alt ?? '')}
        width={imgWidth}
        height={imgHeight}
        loading={priority ? 'eager' : (loading ?? 'lazy')}
        fetchPriority={priority ? 'high' : undefined}
        decoding="async"
        aria-hidden={ariaHidden}
        draggable={draggable}
        className={cn('w-full h-full object-cover', imgClassName)}
      />
    </picture>
  );
}
